/**
 * useVideoRecorder — Records video from a MediaStream using MediaRecorder API.
 *
 * Starts/stops recording when told, returns a Blob when done.
 * Constrained to 720p @ 1Mbps to keep uploads manageable.
 */

import { useState, useRef, useCallback } from 'react';

interface UseVideoRecorderOptions {
  /** The camera MediaStream to record from */
  stream: MediaStream | null;
}

interface UseVideoRecorderResult {
  /** Whether currently recording */
  recording: boolean;
  /** Start recording */
  start: () => void;
  /** Stop recording and return the video Blob */
  stop: () => Promise<Blob | null>;
  /** Duration in seconds since recording started */
  duration: number;
}

export function useVideoRecorder({ stream }: UseVideoRecorderOptions): UseVideoRecorderResult {
  const [recording, setRecording] = useState(false);
  const [duration, setDuration] = useState(0);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const startTimeRef = useRef(0);

  const start = useCallback(() => {
    if (!stream || recording) return;

    chunksRef.current = [];

    // Prefer webm (Chrome/Edge/Firefox), fallback to mp4 (Safari)
    const mimeType = MediaRecorder.isTypeSupported('video/webm;codecs=vp9')
      ? 'video/webm;codecs=vp9'
      : MediaRecorder.isTypeSupported('video/webm')
        ? 'video/webm'
        : 'video/mp4';

    const recorder = new MediaRecorder(stream, {
      mimeType,
      videoBitsPerSecond: 1_000_000, // 1Mbps — keeps file size reasonable
    });

    recorder.ondataavailable = (e) => {
      if (e.data.size > 0) {
        chunksRef.current.push(e.data);
      }
    };

    recorder.start(1000); // Capture in 1s chunks
    recorderRef.current = recorder;
    startTimeRef.current = Date.now();
    setRecording(true);

    // Duration timer
    timerRef.current = setInterval(() => {
      setDuration(Math.floor((Date.now() - startTimeRef.current) / 1000));
    }, 1000);
  }, [stream, recording]);

  const stop = useCallback((): Promise<Blob | null> => {
    return new Promise((resolve) => {
      const recorder = recorderRef.current;
      if (!recorder || recorder.state === 'inactive') {
        resolve(null);
        return;
      }

      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }

      recorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: recorder.mimeType });
        chunksRef.current = [];
        setRecording(false);
        setDuration(0);
        resolve(blob);
      };

      recorder.stop();
    });
  }, []);

  return { recording, start, stop, duration };
}
